import axios from 'axios'
import React, { useEffect, useRef, useState } from 'react'
import { Form, InputGroup, Tabs, Tab, Alert } from 'react-bootstrap'
import { getValidPreviousIntentId } from '../../../../utils/functions/getValidPreviousIntentId'
import { updateInstagramIntent } from '../../../../utils/functions/updateInstagramIntent'

interface IProps {
  intent: any
  inputJson: any[]
  setInputJson: React.Dispatch<React.SetStateAction<any[]>>
  closeModal: () => void
  data: any
}

const FILE_UPLOAD_URL = process.env.REACT_APP_FILE_UPLOAD_URL

const Video: React.FC<IProps> = ({ intent, inputJson, setInputJson, closeModal, data }) => {
  const intentIndex = inputJson.findIndex((item) => item.id === intent.id)
  const [key, setKey] = useState('url')
  const [fileName, setFileName] = useState('No file chosen')
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [videoUrl, setVideoUrl] = useState<string>(intent?.message?.attachment?.payload?.url || '')
  const [intentDescription, setIntentDescription] = useState<string>(
    intent?.intentDescription || ''
  )
  const [isSaving, setIsSaving] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const urlInputRef = useRef<HTMLInputElement>(null)
  const maxLength = 5000000 // 5MB

  const formatSizeInMB = (size: number) => (size / (1024 * 1024)).toFixed(2) + ' MB'

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files: any = event.target.files
    const validFiles: File[] = []

    if (files && files.length > 0) {
      for (let file of files) {
        if (file.size <= maxLength) {
          validFiles.push(file)
          setFileName(file.name)
        } else {
          alert(`File ${file.name} exceeds the 5MB limit.`)
        }
      }

      if (validFiles.length > 0) {
        urlInputRef.current && (urlInputRef.current.value = '')
        setVideoUrl('')
        setUploadedFiles(validFiles)
      }
    }
  }

  const handleFileUploadsAndUpdateJson = async () => {
    if (uploadedFiles.length > 0) {
      const uploadPromises = uploadedFiles.map(async (file) => {
        const response = await axios.post(FILE_UPLOAD_URL as string, {
          files: [{ fileName: file.name, mimeType: file.type }]
        })
        const uploadURL = response.data[0].uploadURL
        await axios.put(uploadURL, file)
        return response.data[0].url
      })

      const urls = await Promise.all(uploadPromises)
      const uploadedUrl = urls[urls.length - 1]
      setVideoUrl(uploadedUrl)

      const newInputJson = [...inputJson]
      newInputJson[intentIndex] = {
        ...intent,
        intentDescription,
        message: {
          attachment: {
            type: 'video',
            payload: { url: uploadedUrl }
          }
        }
      }
      setInputJson(newInputJson)
    }
  }

  useEffect(() => {
    if (uploadedFiles.length > 0) {
      handleFileUploadsAndUpdateJson()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedFiles])

  const updateVideoLink = (link: string) => {
    const newInputJson = [...inputJson]
    newInputJson[intentIndex] = {
      ...intent,
      intentDescription,
      message: {
        attachment: {
          type: 'video',
          payload: { url: link }
        }
      }
    }
    setInputJson(newInputJson)
  }

  const handleUrlBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value && value !== videoUrl) {
      fileInputRef.current && (fileInputRef.current.value = '')
      setFileName('No file chosen')
      setUploadedFiles([])
      setVideoUrl(value)
      updateVideoLink(value)
    }
  }

  const handleSave = async () => {
    const token = localStorage.getItem('auth-token')
    const instagramIDString = data?.instagramIDString
    const previousIntentId = getValidPreviousIntentId(
      intentIndex,
      inputJson,
      data?.WhatsAppInput || []
    )
    setIsSaving(true)
    await updateInstagramIntent({
      token,
      instagramIDString,
      intent,
      intentJson: inputJson[intentIndex],
      previousIntentId,
      onSuccess: closeModal
    })
  }

  const handleTabChange = (k: string | null) => {
    setKey(k || 'url')
  }

  const handleIntentDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setIntentDescription(value)

    const newInputJson = [...inputJson]
    newInputJson[intentIndex] = {
      ...intent,
      ...newInputJson[intentIndex],
      intentDescription: value
    }
    setInputJson(newInputJson)
  }

  return (
    <>
      <h5 className='card-title h4 mb-4'>Video Message</h5>

      <Form.Group className='mb-3'>
        <Form.Label>Intent Description</Form.Label>
        <Form.Control
          type='text'
          placeholder='Enter intent description'
          value={intentDescription}
          onChange={handleIntentDescriptionChange}
        />
      </Form.Group>

      <Tabs id='video-input-tabs' activeKey={key} onSelect={handleTabChange} className='mb-3'>
        <Tab eventKey='url' title='From URL'>
          <InputGroup className='my-3'>
            <InputGroup.Text>Video URL</InputGroup.Text>
            <Form.Control
              ref={urlInputRef}
              type='url'
              placeholder='https://example.com/video.mp4'
              defaultValue={videoUrl}
              onBlur={handleUrlBlur}
            />
          </InputGroup>
        </Tab>

        <Tab eventKey='upload' title='Upload Video'>
          <Form.Group className='my-3'>
            <Form.Label>Select video to upload</Form.Label>
            <Form.Control
              ref={fileInputRef}
              type='file'
              accept='video/*'
              onChange={handleFileInputChange}
            />
            {fileName !== 'No file chosen' && (
              <div className='mt-2 text-muted'>Selected file: {fileName}</div>
            )}
          </Form.Group>
        </Tab>
      </Tabs>

      {videoUrl && (
        <div className='text-center my-4'>
          <video controls style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px' }}>
            <source src={videoUrl} />
            Your browser does not support the video tag.
          </video>
        </div>
      )}

      <div className='d-flex ms-auto mt-auto'>
        <button className='btn btn-secondary me-4' onClick={closeModal}>
          <i className='bi bi-eye-fill' /> Preview
        </button>
        <button className='btn btn-primary' onClick={handleSave}>
          <i className='bi bi-save' /> {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>

      <Alert variant='info' className='mt-4'>
        <strong>WhatsApp Video Guidelines:</strong>
        <ul className='mb-0'>
          <li>HTTPS video URLs are required if using links</li>
          <li>Max file size: 5MB</li>
          <li>Supported formats: MP4, MOV, WebM</li>
        </ul>
      </Alert>
    </>
  )
}

export default Video
